import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { captureException } from "@sentry/react";
import ExpandableChips from "components/ExpandableChips";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "react-toastify";
import LabelEditDialog from "./LabelEditDialog";
import { deleteLabel, type LidarClearanceLabel } from "./labelsApi";
import type { LidarClearanceAxis, LidarClearanceSystem } from "./schemas";
import t from "./translations";

// 0 is reserved by the lidar pipeline for "no axis infringes" (see
// apps/global-api/src/lidar_clearance/labels.py's LABEL_MIN) - a real
// curated label can never use it.
const LABEL_MIN = 1;

type Props = {
  open: boolean;
  system: LidarClearanceSystem;
  axes: LidarClearanceAxis[];
  labels: LidarClearanceLabel[];
  setLabels: Dispatch<SetStateAction<LidarClearanceLabel[]>>;
  loading: boolean;
  onClose: () => void;
};

export default function LabelsDialog({
  open,
  system,
  axes,
  labels,
  setLabels,
  loading,
  onClose,
}: Props) {
  const inUse = system.scene_count > 0;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState<LidarClearanceLabel | null>(
    null
  );

  const axisNameById = useMemo(
    () => new Map(axes.map((axis) => [axis.id, axis.name_en])),
    [axes]
  );

  const nextSuggestedCode = useMemo(
    () =>
      Math.min(
        255,
        Math.max(LABEL_MIN - 1, ...labels.map((label) => label.label)) + 1
      ),
    [labels]
  );

  const rows = useMemo(
    () =>
      labels.map((label) => ({
        label,
        chipLabelMap: new Map(
          label.axis_values.map((axisValue) => [
            axisValue.axis_id,
            `${axisNameById.get(axisValue.axis_id) ?? axisValue.axis_id}=${axisValue.axis_value}`,
          ])
        ),
      })),
    [labels, axisNameById]
  );

  // A label curating more than one axis is auto-generated ("combined") rather
  // than individually curated - collapsed by default so the table stays
  // readable on systems with a large cartesian-product fill.
  const directRows = useMemo(
    () => rows.filter(({ label }) => label.axis_values.length <= 1),
    [rows]
  );
  const combinedRows = useMemo(
    () => rows.filter(({ label }) => label.axis_values.length > 1),
    [rows]
  );

  const mergeLabel = (saved: LidarClearanceLabel) => {
    setLabels((prev) =>
      [...prev.filter((label) => label.id !== saved.id), saved].sort(
        (a, b) => a.id - b.id
      )
    );
  };

  const openCreate = () => {
    setEditingLabel(null);
    setEditorOpen(true);
  };

  const openEdit = (label: LidarClearanceLabel) => {
    setEditingLabel(label);
    setEditorOpen(true);
  };

  const handleDelete = async (labelId: number) => {
    try {
      await deleteLabel(system.region, system.id, labelId);
      setLabels((prev) => prev.filter((label) => label.id !== labelId));
    } catch (error) {
      captureException(error, {
        tags: { feature: "lidarClearance.deleteLabel" },
      });
      toast.error(t.FailedToDeleteLabel);
    }
  };

  const renderLabelRow = ({
    label,
    chipLabelMap,
  }: (typeof rows)[number]) => (
    <TableRow
      key={label.id}
      hover
      onClick={() => openEdit(label)}
      sx={{ cursor: "pointer" }}
    >
      <TableCell>{label.label}</TableCell>
      <TableCell>{label.label_name ?? "—"}</TableCell>
      <TableCell>
        {label.axis_values.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t.AllZeroCombination}
          </Typography>
        ) : (
          <ExpandableChips
            ids={label.axis_values.map((av) => av.axis_id)}
            labelMap={chipLabelMap}
            max={6}
          />
        )}
      </TableCell>
      <TableCell>
        <Tooltip title={inUse ? t.SystemInUseHint : ""}>
          <span>
            <IconButton
              size="small"
              aria-label={t.RemoveLabel}
              disabled={inUse}
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(label.id);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  const labelsTableHead = (
    <TableHead>
      <TableRow>
        <TableCell sx={{ width: 90 }}>{t.LabelCodeLabel}</TableCell>
        <TableCell sx={{ width: 220 }}>{t.LabelNameLabel}</TableCell>
        <TableCell>{t.LabelAxisValuesColumn}</TableCell>
        <TableCell sx={{ width: 48 }} />
      </TableRow>
    </TableHead>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        sx={{ "& .MuiDialog-paper": { height: "80vh" } }}
        data-testid="labels-dialog"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>{t.LabelsTitle}</Box>
          <Button size="small" onClick={openCreate}>
            {t.NewLabelButton}
          </Button>
          <IconButton onClick={onClose} aria-label={t.CancelButton}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", p: 0 }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : labels.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              {t.NoLabelsYet}
            </Typography>
          ) : (
            <Box sx={{ flexGrow: 1, overflow: "auto" }}>
              <TableContainer>
                <Table size="small" stickyHeader>
                  {labelsTableHead}
                  <TableBody>{directRows.map(renderLabelRow)}</TableBody>
                </Table>
              </TableContainer>

              {combinedRows.length > 0 && (
                <Accordion disableGutters>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    data-testid="combined-labels-summary"
                  >
                    <Typography variant="body2">
                      {t.CombinedLabelsSummary(combinedRows.length)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        {labelsTableHead}
                        <TableBody>{combinedRows.map(renderLabelRow)}</TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <LabelEditDialog
        open={editorOpen}
        axes={axes}
        region={system.region}
        systemId={system.id}
        initial={editingLabel}
        locked={inUse}
        suggestedNextCode={nextSuggestedCode}
        onClose={() => setEditorOpen(false)}
        onSaved={mergeLabel}
      />
    </>
  );
}
